import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import SchemaForm from '../SchemaForm/SchemaForm';
import { Button, Container, FormControl, Grid, InputLabel, MenuItem, Paper, Select, Typography, SelectChangeEvent } from '@mui/material';
import { getWorkflows } from '../../../redux/slices/profileSlice';
import { selectWorkflows } from '../../../redux/selectors/profileSelectors';
import TEST_SCHEMAS from './test_schemas.json';
import TEST_SCHEMAS_VALUES from './test_schemas_values.json';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { selectUserEmail } from '@/redux/selectors/authSelectors';
import { useHistory } from 'react-router-dom';

const SchemaPlayground = () => {
  const dispatch = useAppDispatch();
  const workflows = useAppSelector(selectWorkflows);
  const [schemaInput, setSchemaInput] = useState<string>('');
  const [parsedSchema, setParsedSchema] = useState<any>();
  const [errors, setErrors] = useState<string>('');
  const [selectedWorkflow, setSelectedWorkflow] = useState<string>('');
  const [selectedWorkflowVariant, setSelectedWorkflowVariant] = useState<string>('');
  const [formValues, setFormValues] = useState<any>({});
  const [selectedTestSchema, setSelectedTestSchema] = useState<string>('');
  const schemaRef = React.useRef<any>(null);
  const userEmail = useAppSelector(selectUserEmail);
  const history = useHistory();

  useEffect(() => {
    if (!(userEmail || '').includes('tmtinsights')) {
      history.replace('/no-access');
    }
  }, [userEmail]);

  useEffect(() => {
    dispatch(getWorkflows());
  }, []);

  useEffect(() => {
    if (!schemaInput?.length) {
      setParsedSchema(null);
      setErrors('');
      setSelectedWorkflow('');
      setSelectedWorkflowVariant('');
      setFormValues({});
      return;
    }
    try {
      const parsedCode = JSON.parse(schemaInput);
      setParsedSchema(parsedCode);
      setErrors('');
      // ...do something with it and return a React element
    } catch (err: any) {
      console.log(err.message);
      setErrors(err.message);
    }
  }, [schemaInput]);

  useEffect(() => {
    if (selectedWorkflow && selectedWorkflowVariant) {
      const workflow = workflows[selectedWorkflow];
      const schema = workflow[selectedWorkflowVariant];
      setSchemaInput(JSON.stringify(schema, null, 2));
    }
  }, [selectedWorkflowVariant]);

  const handleWorkflowChange = (e: SelectChangeEvent) => {
    setSelectedWorkflow(e.target.value as string);
    setSelectedWorkflowVariant('');
    setSelectedTestSchema('');
  };

  const handleWorkflowVariantChange = (e: SelectChangeEvent) => {
    setSelectedWorkflowVariant(e.target.value as string);
    setSelectedTestSchema('');
  };

  const selectTestSchema = (e: SelectChangeEvent) => {
    setSelectedTestSchema(e.target.value as string);
    setSelectedWorkflow('');
    setSelectedWorkflowVariant('');
    if (e.target.value) {
      setSchemaInput(JSON.stringify(TEST_SCHEMAS[e.target.value as keyof typeof TEST_SCHEMAS], null, 2));
      setFormValues(TEST_SCHEMAS_VALUES[e.target.value as keyof typeof TEST_SCHEMAS_VALUES]);
    }
  };

  return (
    <Container>
      <Grid item container justifyContent="space-between" xs={12} sx={{ mt: 6 }}>
        <div>
          <Typography variant="h1" sx={{ pb: 0 }}>
            Schema Playground
          </Typography>
          <Typography variant="caption" sx={{ fontSize: '.7rem' }}>
            fuck around and find out
          </Typography>
        </div>
        <div>
          <Grid container alignItems="center">
            <FormControl sx={{ mr: 1, width: 300 }} variant="filled">
              <InputLabel id="testschemas">Test Schemas</InputLabel>
              {/* dropdown with option name being the key from TEST_SCHEMAS and value being value of the key */}
              <Select value={selectedTestSchema} onChange={selectTestSchema}>
                <MenuItem value="">none</MenuItem>
                {Object.keys(TEST_SCHEMAS).map((schemaKey) => (
                  <MenuItem key={schemaKey} value={schemaKey}>
                    {schemaKey}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Grid item mx={2} mr={3}>
              - OR -
            </Grid>
            <FormControl sx={{ mr: 1, width: 300 }} variant="filled">
              <InputLabel id="schema-playground-wf-label">Workflow</InputLabel>
              <Select
                labelId="schema-playground-wf-label"
                id="schema-playground-wf"
                value={selectedWorkflow}
                label="Workflow"
                onChange={handleWorkflowChange}
              >
                <MenuItem value="">none</MenuItem>
                {Object.keys(workflows)?.map((key) => (
                  <MenuItem key={key} value={key}>
                    {workflows[key].name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ width: 150 }} variant="filled">
              <InputLabel id="schema-playground-wf-label">Schema Type</InputLabel>
              <Select
                disabled={!selectedWorkflow}
                labelId="schema-playground-wf-label"
                id="schema-playground-wf"
                value={selectedWorkflowVariant}
                label="Schema Type"
                onChange={handleWorkflowVariantChange}
              >
                {['instance_schema', 'title_schema', 'template_schema', 'order_spec_schema', 'order_profile_schema'].map((type) => {
                  const typeProps = workflows?.[selectedWorkflow]?.[type]?.properties;
                  return (
                    <MenuItem key={type} value={type} disabled={typeProps && !Object.keys(typeProps).length}>
                      {type}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>
        </div>
      </Grid>
      <Grid container sx={{ mt: 2 }} spacing={5}>
        <Grid item xs={12} md={5}>
          <Typography variant="subtitle1">JSON Schema:</Typography>
          <InputTextArea name="schema" id="schema" value={schemaInput} onChange={(e) => setSchemaInput(e.target.value)} rows={50} />
          <Grid item xs={12} sx={{ color: 'error.main' }}>
            <code>{errors}</code>
          </Grid>
          <Grid item xs={12} sx={{ mt: 3 }}>
            <Typography variant="subtitle1">Form Values:</Typography>
            <InputTextArea
              value={JSON.stringify(formValues, null, 2)}
              onChange={(e) => setFormValues(JSON.parse(e.target.value))}
              style={{ width: '100%', height: '200px' }}
            />
          </Grid>
          <Button variant="contained" color="primary" sx={{ mt: 2 }} onClick={() => schemaRef.current?.submit()}>
            Validate
          </Button>
        </Grid>

        <Grid item container xs={12} md={7} flexDirection="column">
          <Grid item>
            <Typography variant="subtitle1">Output:</Typography>
            <Paper sx={{ p: 3 }}>
              {parsedSchema ? (
                <SchemaForm formRef={schemaRef} schema={parsedSchema} formData={formValues} onChange={(form) => setFormValues(form.formData)} />
              ) : (
                'No schema to display'
              )}
            </Paper>
          </Grid>
        </Grid>
      </Grid>
    </Container>
  );
};

export default SchemaPlayground;

const InputTextArea = styled.textarea`
  background: ${({ theme }) => theme.palette.background.paper};
  color: ${({ theme }) => theme.palette.text.primary};
  width: 100%;
`;
